/**
 * PaymentAttemptManager
 * Owns the current payment attempt, the session's attempt audit log, and
 * whether a submission is currently being processed.
 */

import { generateGuid } from '@anddone/ajs-util';
import type { PaymentAttemptLog, TransactionStatus } from '@anddone/ajs-util';

export class PaymentAttemptManager {
  // The most-recent payment attempt.
  #paymentAttemptId: string | null = null;
  // Append-only audit log of every payment attempt in this session.
  readonly #log: PaymentAttemptLog[] = [];
  #isProcessing = false;

  public get paymentAttemptId(): string | null {
    return this.#paymentAttemptId;
  }

  /**
   * Snapshot of the payment attempt audit log.
   * Returns frozen copies so external readers cannot rewrite recorded history.
   */
  public get log(): readonly PaymentAttemptLog[] {
    return Object.freeze(this.#log.map(attempt => Object.freeze({ ...attempt })));
  }

  public get isProcessing(): boolean {
    return this.#isProcessing;
  }

  /**
   * Start a new payment attempt, record it in the log, and mark a submission
   * as in progress.
   *
   * @returns The new paymentAttemptId
   */
  public create(): string {
    this.#paymentAttemptId = generateGuid();
    this.#log.push({
      paymentAttemptId: this.#paymentAttemptId,
      status: 'initialized',
      timestamp: Date.now(),
    });

    this.#isProcessing = true;

    return this.#paymentAttemptId;
  }

  /**
   * Clear the current payment attempt and end any in-progress submission.
   * The audit log is kept.
   *
   * @returns void
   */
  public reset(): void {
    this.#paymentAttemptId = null;
    this.#isProcessing = false;
  }

  /**
   * Mark the in-progress submission as finished without clearing the attempt.
   *
   * @returns void
   */
  public endProcessing(): void {
    this.#isProcessing = false;
  }

  /**
   * Update the log entry for the current payment attempt.
   *
   * @returns true if the payment attempt was found and updated, false if not found
   */
  public update(status: TransactionStatus, error?: string): boolean {
    const attempt = this.#log.find(a => a.paymentAttemptId === this.#paymentAttemptId);
    if (attempt) {
      attempt.status = status;
      attempt.timestamp = Date.now();
      attempt.error = error;
      return true;
    }
    return false;
  }
}

Object.freeze(PaymentAttemptManager.prototype);
